import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import api from "api/api";
import endpoint from "api/endpoints";

import ToastNotification from "../../common/toast/Toast";

/* ---------- helpers: localStorage ---------- */
const LS_KEY = "favorites";

const loadFavoritesFromStorage = async (
  userInfo = null,
  getAccessTokenSilently = null
) => {
  try {
    // Check if userInfo exists and has id
    if (userInfo && userInfo.id) {
      // Check if backend favorites data exists AND has meaningful content
      if (userInfo.custentity_favorite_item) {
        try {
          const backendFavoritesData = initializeFavorites(
            userInfo.custentity_favorite_item
          );
          // Only use backend data if it's a non-empty array
          if (
            Array.isArray(backendFavoritesData) &&
            backendFavoritesData.length > 0
          ) {
            // Sync backend data to localStorage
            localStorage.setItem(LS_KEY, JSON.stringify(backendFavoritesData));
            return backendFavoritesData;
          }
        } catch {
          // If parsing fails, just return empty
          return [];
        }
      }

      // If backend favorites is empty, clear localStorage and return empty favorites
      localStorage.removeItem(LS_KEY);
      return [];
    } else {
      // If no userInfo, just return empty favorites (don't clear localStorage)
      return [];
    }
  } catch {
    // Return empty favorites on any error (don't clear localStorage)
    return [];
  }
};

function loadFromLS() {
  try {
    const raw = JSON.parse(localStorage.getItem(LS_KEY) || "[]");
    return Array.isArray(raw) ? raw.map(Number).filter((n) => !isNaN(n)) : [];
  } catch {
    return [];
  }
}
const saveFavoriteToStorage = (ids) => {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(ids || []));
  } catch {}
};

// Debounced sync function - only sync the LAST action after user stops
let favoritesTimeout = null;

const syncToBackendWithTimeout = () => {
  // Clear the previous timeout if it exists
  if (favoritesTimeout) {
    clearTimeout(favoritesTimeout);
  }

  // Set a new timeout - this will only execute if no new actions happen within 1 second
  favoritesTimeout = setTimeout(async () => {
    try {
      if (!favoriteStoreGetState) return;

      const state = favoriteStoreGetState();
      const userInfo = state?.user?.info;
      const localStorageData = localStorage.getItem(LS_KEY);

      if (userInfo?.id && localStorageData) {
        // Get access token if available
        const token = favoriteTokenGetter ? await favoriteTokenGetter() : null;
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        // Parse favorites from localStorage
        let favorites = [];
        try {
          favorites = JSON.parse(localStorageData);
          if (!Array.isArray(favorites)) {
            favorites = [];
          }
        } catch {
          favorites = [];
        }

        // Check which favorites are valid items
        let validIds = [];
        if (favorites.length > 0) {
          try {
            const response = await api.post(
              endpoint.POST_CHECK_ITEM_IDS(),
              { ids: favorites },
              { headers }
            );
            // Extract IDs from response
            validIds = response.data || [];
          } catch {
            // If check fails, use all favorites
            validIds = favorites.map((id) => ({ id }));
          }
        }

        // Convert to NetSuite format
        const favoritesData = {
          items: validIds,
        };

        api
          .patch(
            endpoint.PATCH_UPDATE_CUSTOMER(),
            {
              custentity_favorite_item: favoritesData,
            },
            { headers }
          )
          .catch(() => {
            // Silently fail - favorites will still work with localStorage
          });
      }
    } catch {
      // Silently fail
    }
  }, 1000); // 1 second debounce delay
};

// Store reference to getState and getAccessTokenSilently for backend sync
let favoriteStoreGetState = null;
let favoriteTokenGetter = null;

export const setFavoriteStoreRef = (store, getAccessTokenSilently = null) => {
  favoriteStoreGetState = store.getState;
  favoriteTokenGetter = getAccessTokenSilently;
};

/* ---------- parse whatever backend returns ---------- */
const initializeFavorites = (raw) => {
  if (!raw) return [];
  if (typeof raw === "string") {
    return raw
      .split(",")
      .map((x) => Number(String(x).trim()))
      .filter((n) => !isNaN(n));
  }
  if (Array.isArray(raw)) {
    return raw.map((x) => Number(x)).filter((n) => !isNaN(n));
  }
  if (typeof raw === "object" && Array.isArray(raw.items)) {
    return raw.items.map((x) => Number(x?.id ?? x)).filter((n) => !isNaN(n));
  }
  return [];
};

/* ---------- async thunks ---------- */
export const loadFavoritesAsync = createAsyncThunk(
  "favorites/loadFavoritesAsync",
  async ({ userInfo = null, getAccessTokenSilently = null } = {}) => {
    return await loadFavoritesFromStorage(userInfo, getAccessTokenSilently);
  }
);

export const addToFavorites = createAsyncThunk(
  "favorites/addToFavorites",
  async (itemId, { getState, rejectWithValue }) => {
    const state = getState();
    const userInfo = state?.user?.info;

    if (!userInfo?.id) {
      ToastNotification.error("Please sign in to add items to favorites");
      return rejectWithValue("Not logged in");
    }

    const id = Number(itemId);
    if (isNaN(id)) {
      return rejectWithValue("Invalid item ID");
    }

    return id;
  }
);

export const removeFromFavorites = createAsyncThunk(
  "favorites/removeFromFavorites",
  async (itemId, { getState, rejectWithValue }) => {
    const state = getState();
    const userInfo = state?.user?.info;

    if (!userInfo?.id) {
      ToastNotification.error("Please sign in to manage favorites");
      return rejectWithValue("Not logged in");
    }

    const id = Number(itemId);
    if (isNaN(id)) {
      return rejectWithValue("Invalid item ID");
    }

    return id;
  }
);

/* ---------- slice ---------- */
const favoritesSlice = createSlice({
  name: "favorites",
  initialState: {
    items: loadFromLS(), // number[]
    loading: false,
  },
  reducers: {
    setFavorites: (state, action) => {
      state.items = (action.payload || []).map(Number).filter((n) => !isNaN(n));
      saveFavoriteToStorage(state.items);
      syncToBackendWithTimeout();
    },

    loadFavorites: (state) => {
      // Synchronous fallback - load from localStorage only
      try {
        state.items = loadFromLS();
      } catch {
        state.items = [];
      }
    },

    setLoading: (state, action) => {
      state.loading = !!action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadFavoritesAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(loadFavoritesAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.items = Array.isArray(action.payload)
          ? action.payload.map(Number).filter((n) => !isNaN(n))
          : [];
        saveFavoriteToStorage(state.items);
      })
      .addCase(loadFavoritesAsync.rejected, (state) => {
        state.loading = false;
        // Fallback to localStorage on error
        try {
          state.items = loadFromLS();
        } catch {
          state.items = [];
        }
      })
      // addToFavorites thunk
      .addCase(addToFavorites.fulfilled, (state, action) => {
        const id = action.payload;
        if (!state.items.includes(id)) {
          state.items.push(id);
          saveFavoriteToStorage(state.items);
          syncToBackendWithTimeout();
        }
      })
      // removeFromFavorites thunk
      .addCase(removeFromFavorites.fulfilled, (state, action) => {
        const id = action.payload;
        state.items = state.items.filter((itemId) => itemId !== id);
        saveFavoriteToStorage(state.items);
        syncToBackendWithTimeout();
      });
  },
});

export const {
  setFavorites,
  loadFavorites,
  setLoading: setFavoritesLoading,
} = favoritesSlice.actions;

// Export the storage helper function
export { loadFavoritesFromStorage };

// Selectors
export const selectFavoritesLoading = (state) => state.favorites.loading;
export const selectFavorites = (state) => state.favorites.items;

export default favoritesSlice.reducer;
